import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import * as fbs from "@/api/fbs";
import * as settingsApi from "@/api/system_settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastError } from "@/lib/api-errors";

/** Принтеры этикеток по назначениям разделов.
 *
 * Список назначений фильтрует СЕРВЕР (`purposes_for(pages)`) — вторая копия
 * правила «какой раздел печатает» разъехалась бы здесь молча. Статус
 * relay-агентов — отдельная карточка страницы «Печать», статус агента
 * точки сборки — в её карточке, там же, где её принтер. */
export function PrinterCard(): React.ReactElement {
  const qc = useQueryClient();
  const { data: printers } = useQuery({
    queryKey: ["label-printers"],
    queryFn: settingsApi.listPrinters,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Принтеры этикеток</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Принтер настраивается ОТДЕЛЬНО для каждого раздела: они стоят в разных
          помещениях, и этикетка паллеты не должна уезжать в комнату ФБС. Адреса могут
          совпадать — тогда просто укажите один и тот же. Печать идёт через relay-агента
          склада: сервер не видит локальную сеть напрямую.
        </p>
        {(printers ?? []).map((p) => (
          <PrinterRow key={p.purpose} printer={p} onSaved={() => {
            qc.invalidateQueries({ queryKey: ["label-printers"] });
            qc.invalidateQueries({ queryKey: ["fbs-print-status"] });
          }} />
        ))}
      </CardContent>
    </Card>
  );
}

function PrinterRow({
  printer,
  onSaved,
}: {
  printer: settingsApi.PrinterView;
  onSaved: () => void;
}): React.ReactElement {
  const [target, setTarget] = useState(printer.target ?? "");

  useEffect(() => {
    setTarget(printer.target ?? "");
  }, [printer]);

  const saveMut = useMutation({
    mutationFn: () => settingsApi.setPrinter(printer.purpose, { target: target || null }),
    onSuccess: () => { onSaved(); toast.success("Сохранено"); },
    onError: toastError("Ошибка сохранения"),
  });

  const testMut = useMutation({
    mutationFn: () => fbs.printTestLabel(printer.purpose),
    onSuccess: (r) =>
      r.ok
        ? toast.success("Задание ушло на принтер — проверьте, что этикетка СКАНИРУЕТСЯ")
        : toast.error(r.error ?? "Печать не удалась"),
    onError: toastError("Ошибка печати"),
  });

  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-medium">{printer.title}</div>
        <span
          className={
            printer.configured
              ? "rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800"
              : "rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
          }
        >
          {printer.configured ? printer.target : "не настроен"}
        </span>
      </div>
      <div>
        <Label className="text-xs">Адрес принтера</Label>
        <Input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="tcp://192.168.1.50:9100"
        />
        <p className="mt-0.5 text-xs text-muted-foreground">
          сетевой — <code>tcp://адрес:9100</code>, USB —{" "}
          <code>usb:///dev/usb/lp0</code>, COM-порт — <code>serial://COM3</code>
        </p>
      </div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
          Сохранить
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => testMut.mutate()}
          disabled={testMut.isPending || !printer.configured}
        >
          <Printer className="mr-1 size-4" /> Тестовая печать
        </Button>
      </div>
    </div>
  );
}
